import { toInt, toBool, lockBodyScroll, unlockBodyScroll } from "./utils.js";

class ProductVariantSelector extends HTMLElement {
  #isPaper = false;

  #ebookVariantId = null;
  #paperVariantId = null;
  #ebookPrice = null;
  #paperPrice = null;
  #ebookComparePrice = null;
  #paperComparePrice = null;
  #forcePaper = false;
  #forceEbook = false;
  #siblingMode = false;
  #hasSizePicker = false;
  #deliveryEbook = "";
  #deliveryPaper = "";

  #ebookBtn = null;
  #paperBtn = null;
  #deliveryInfo = null;
  #dialog = null;

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error("[ProductVariantSelector:init]", error);
    }
  }

  #init() {
    this.#readData();
    this.#bindElements();
    this.#bindEvents();
    if (this.#forcePaper) {
      this.#switchVariant(true);
    } else if (this.#forceEbook) {
      this.#switchVariant(false);
    }
  }

  #readData() {
    const ds = this.dataset;
    this.#ebookVariantId = toInt(ds.ebookVariantId);
    this.#paperVariantId = toInt(ds.paperVariantId);
    this.#ebookPrice = JSON.parse(ds.ebookPrice);
    this.#paperPrice = JSON.parse(ds.paperPrice);
    this.#ebookComparePrice = JSON.parse(ds.ebookComparePrice);
    this.#paperComparePrice = JSON.parse(ds.paperComparePrice);
    this.#forcePaper = toBool(ds.forcePaper);
    this.#forceEbook = toBool(ds.forceEbook);
    this.#siblingMode = toBool(ds.siblingMode);
    this.#hasSizePicker = toBool(ds.hasSizePicker);
    this.#isPaper = toBool(ds.isPaperActive);
    this.#deliveryEbook = ds.deliveryEbook ?? "";
    this.#deliveryPaper = ds.deliveryPaper ?? "";
  }

  #bindElements() {
    this.#ebookBtn = this.querySelector('[data-variant-option="ebook"]');
    this.#paperBtn = this.querySelector('[data-variant-option="paper"]');
    this.#deliveryInfo = this.querySelector("[data-delivery-info]");
    this.#dialog = this.querySelector("[data-purchased-dialog]");
  }

  #bindEvents() {
    // Late-joining listeners (e.g. the add-to-cart button, which upgrades after
    // this element) can miss the initial variant:change fired during init. Reply
    // to their request with the current state so they reconcile regardless of
    // element upgrade order.
    document.addEventListener("variant:request", () => {
      // Paper products with size variants (SEWR-T170): the size picker owns the
      // variant state and answers variant:request itself — a second reply with
      // the server-rendered default would reset the visitor's size selection.
      if (this.#siblingMode && this.#hasSizePicker) return;
      this.#dispatchVariantChange(this.#isPaper);
    });

    // Sibling-Modus (SEWR-T170): Das andere Format ist ein Link auf sein eigenes
    // Produkt, es gibt nichts umzuschalten. Ein Click-Handler wuerde hier
    // zusaetzlich zur Navigation die URL umschreiben. Der variant:change-Dispatch
    // oben bleibt, weil Preis, Media, Add-to-Cart und Lizenz-Block sich beim
    // Laden dagegen abgleichen.
    if (this.#siblingMode) return;

    this.#dialog
      ?.querySelector("[data-close-dialog]")
      ?.addEventListener("click", () => this.#dialog.close());
    this.#dialog?.addEventListener("click", (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    });
    // Native `close` fires for ESC, close button and backdrop click alike.
    this.#dialog?.addEventListener("close", () => {
      if (!window.Shopify?.designMode) unlockBodyScroll();
    });

    this.#ebookBtn?.addEventListener("click", () => {
      if (this.#ebookBtn.hasAttribute("data-digital-disabled")) {
        if (!window.Shopify?.designMode) lockBodyScroll();
        this.#dialog?.showModal();
        return;
      }
      if (!this.#isPaper) return;
      this.#switchVariant(false);
    });

    this.#paperBtn?.addEventListener("click", () => {
      if (this.#isPaper) return;
      this.#switchVariant(true);
    });
  }

  #dispatchVariantChange(toPaper) {
    const variantId = toPaper ? this.#paperVariantId : this.#ebookVariantId;
    const price = toPaper ? this.#paperPrice : this.#ebookPrice;
    const comparePrice = toPaper ? this.#paperComparePrice : this.#ebookComparePrice;
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variantId,
          price,
          comparePrice,
          isDigital: !toPaper,
        },
      }),
    );
  }

  #switchVariant(toPaper) {
    this.#isPaper = toPaper;
    if (this.#ebookBtn) {
      this.#ebookBtn.setAttribute("aria-pressed", String(!toPaper));
      if (toPaper) {
        this.#ebookBtn.classList.remove("bg-(--ebook-active-bg)", "text-(--ebook-active-text)");
        this.#ebookBtn.classList.add("bg-white", "text-gray-700", "hover:bg-gray-50");
      } else {
        this.#ebookBtn.classList.add("bg-(--ebook-active-bg)", "text-(--ebook-active-text)");
        this.#ebookBtn.classList.remove("bg-white", "text-gray-700", "hover:bg-gray-50");
      }
    }

    if (this.#paperBtn) {
      this.#paperBtn.setAttribute("aria-pressed", String(toPaper));
      if (toPaper) {
        this.#paperBtn.classList.add("bg-(--paper-active-bg)", "text-(--paper-active-text)");
        this.#paperBtn.classList.remove("bg-white", "text-gray-700", "hover:bg-gray-50");
      } else {
        this.#paperBtn.classList.remove("bg-(--paper-active-bg)", "text-(--paper-active-text)");
        this.#paperBtn.classList.add("bg-white", "text-gray-700", "hover:bg-gray-50");
      }
    }

    if (this.#deliveryInfo) {
      this.#deliveryInfo.innerHTML = `<p class="font-medium">${toPaper ? this.#deliveryPaper : this.#deliveryEbook}</p>`;
    }

    const variantId = toPaper ? this.#paperVariantId : this.#ebookVariantId;
    const url = new URL(window.location);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url);

    this.#dispatchVariantChange(toPaper);
  }
}

if (!customElements.get("product-variant-selector")) {
  customElements.define("product-variant-selector", ProductVariantSelector);
}
